import { takeEvery, call, put } from 'redux-saga/effects'
import CryptoJS from 'crypto-js'
import {
  API_SERVER,
  GET_TAX_CARD,
  POST_YEL_START,
  GET_YEL_START,
  GET_USER_YEL_INFO,
  ON_PASSWORD_UPDATE,
  UPDATE_TAX_CARD
} from '../constants'
import { apiRequest, apiManualPost } from '../utils/request'
import {
  getYelSuccess,
  getYelFailed,
  passwordUpdateSuccess,
  passwordUpdateFailed,
  yelUpdateSuccess,
  yelUpdateFailed,
  getUserYelInfo,
  enableYelIncomeLock,
  closeTaxCardSpinner,
  updateTaxCardSuccess,
  updateTaxCardFailed,
  getUserTaxInfo
} from '../actions'
import store from '../store'
import { updateFormValues } from '../form'
import { defaultValues as taxCardDefaultValues } from '../components/profile/tax.component'

/**
 * @author Pragya Gupta
 *
 */

function* getUserYelInfoSaga({ userData }) {
  try {
    // PI-301 Clear or show YEL component when searching user email
    const userType = store.getState().client.user.data[5]
    const uuid =
      userType == 2
        ? userData?.uuid ?? store.getState().adminUser.selectedUser.uuid
        : store.getState().client.user.data[2]

    if (!!uuid) {
      const url = `${API_SERVER}/GetUserYelInfo`
      const body = JSON.stringify({
        uuid: uuid
      })
      const result = yield call(apiManualPost, url, body)
      const resultParsed = JSON.parse(result.data)

      if (
        resultParsed[0].yelSelect === 'yel_minimum' &&
        resultParsed[0].yel_income > 0
      ) {
        //console.log('Inside getUserYelInfoSaga:: ')
        yield put(enableYelIncomeLock())
      }

      yield put(updateFormValues('yel', resultParsed[0]))
    }
  } catch (e) {
    //yield put(loadUserYelInfoFailed(e))
    // console.warn(e)
  }
}

function* getTaxCardSaga({ userData }) {
  try {
    const userType = store.getState().client.user.data[5]
    const uuid =
      userType == 2
        ? userData?.uuid ?? store.getState().adminUser.selectedUser.uuid
        : store.getState().client.user.data[2]

    if (!!uuid) {
      const url = `${API_SERVER}/GetTaxCard`
      const body = {
        uuid: uuid
      }

      const result = yield call(apiManualPost, url, JSON.stringify(body))
      const resultParsed = JSON.parse(result.data)
      if (resultParsed.length > 0) {
        yield put(updateFormValues('tax', resultParsed[0]))
      } else {
        yield put(updateFormValues('tax', taxCardDefaultValues))
      }
    }
  } catch (e) {
    // console.warn('no tax card')
  }
}

function* updateTaxCardSaga({ formValues, disableLoader }) {
  try {
    const userType = store.getState().client.user.data[5]
    const uuid =
      userType == 2
        ? store.getState().adminUser.selectedUser.uuid
        : store.getState().client.user.data[2]

    if (!!uuid) {
      const url = `${API_SERVER}/UpdateTaxCard`
      const body = {
        uuid: uuid,
        startingBalance: formValues ? formValues.startingBalance ?? 0 : null
      }

      const result = yield call(apiManualPost, url, JSON.stringify(body))

      if (result.data === 'APIError: No tax card found') {
        yield put(updateTaxCardFailed(disableLoader, true))
      } else {
        const resultParsed = JSON.parse(result.data)
        if (resultParsed.length > 0) {
          if (formValues) {
            yield put(updateFormValues('tax', resultParsed[0]))
            yield put(updateTaxCardSuccess(disableLoader))
          }
          const pathname = store.getState().router.location.pathname
          if (pathname === '/dashboard/main') {
            yield put(getUserTaxInfo())
          }
        }
      }
    }
    yield put(closeTaxCardSpinner())
  } catch (e) {
    yield put(updateTaxCardFailed(disableLoader))
    yield put(closeTaxCardSpinner())
    //console.warn(e)
  }
}

function* postYelSaga({ formValues }) {
  try {
    const url = `${API_SERVER}/UpdateuserYelInfo`
    const uuid = store.getState().client.user.data[2]

    const userType = store.getState().client.user.data[5]
    const { adminUser } = store.getState()
    const { selectedUser } = adminUser

    /*     PI-344,The YEL contribution which ASPA is allowing now is just 2 option in the beginning
    Additional parameters can be added based on business logic
    Uncomment the following section if needed */

    //Update for firsttime_enterprenuer == false
    const body = JSON.parse(
      JSON.stringify({
        ...formValues,
        uuid: userType == 2 ? selectedUser.uuid : uuid,
        yel_income: formValues.yel_income
          .toString()
          .replace(/\s+/g, '')
          .replace(',', '.')
      })
    )

    const result = yield call(apiManualPost, url, JSON.stringify(body))
    if (result.data === 'User yelinfo updated successfully!') {
      yield put(yelUpdateSuccess(result.data))
    } else {
      yield put(yelUpdateFailed(result.data))
    }
    yield put(getUserYelInfo())
  } catch (e) {
    //console.log('error here: ', e)
    yelUpdateFailed(e)
  }
}

function* getYelSaga() {
  try {
    const year = new Date().getFullYear()
    const url = `${API_SERVER}/yels/${year}`
    const result = yield apiRequest(url)
    yield put(getYelSuccess(result))
  } catch (e) {
    yield put(getYelFailed())
  }
}

/**
 * updatePasswordSaga of employer side is copied to develop separately with user side
 * if this original is updated, let check and done the same things with employer side
 */

function* updatePasswordSaga({ formValues }) {
  try {
    const hashedOldPassword = CryptoJS.SHA256(formValues.current_pw).toString()
    const hashedPassword = CryptoJS.SHA256(formValues.new_pw).toString()
    const uuid = store.getState().client.user.data[2]
    const userType = store.getState().client.user.data[5]
    const { adminUser } = store.getState()
    const { selectedUser } = adminUser

    const body = JSON.stringify({
      oldpassword: hashedOldPassword,
      password: hashedPassword,
      uuid: userType == 2 ? selectedUser.uuid : uuid
    })

    const url = `${API_SERVER}/UpdateUserCredentials`
    const result = yield call(apiManualPost, url, body)
    if (result.data === 'user password updated successfully!') {
      yield put(passwordUpdateSuccess(result.data))
    } else {
      yield put(passwordUpdateFailed(result.data))
    }
  } catch (e) {
    passwordUpdateFailed(e)
  }
}

export function* watchGetTaxCardSaga() {
  yield takeEvery(GET_TAX_CARD, getTaxCardSaga)
}

export function* watchUpdateTaxCardSaga() {
  yield takeEvery(UPDATE_TAX_CARD, updateTaxCardSaga)
}

export function* watchPostYelSaga() {
  yield takeEvery(POST_YEL_START, postYelSaga)
}

export function* watchGetUserYelInfoSaga() {
  yield takeEvery(GET_USER_YEL_INFO, getUserYelInfoSaga)
}

export function* watchGetYelSaga() {
  yield takeEvery(GET_YEL_START, getYelSaga)
}

export function* watchUpdatePasswordSaga() {
  yield takeEvery(ON_PASSWORD_UPDATE, updatePasswordSaga)
}
